// AI Generated Component
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PageRepoService } from '@client/app/services/api/page-repo/page-repo.service';
import { MonNumberPipe } from '@monsido/core/locale/pipes/mon-number.pipe';
import { BaseEntityService } from '@client/app/blocks/base/base.entity.service';
import { ENTITY_CONSTANT } from '@client/app/core/constants/entity.constant';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';

/**
 * Inventory content pages component
 */
@Component({
    selector: 'inventory-content-pages',
    templateUrl: './inventory-content-pages.component.html',
    standalone: false,
})
export class InventoryContentPagesComponent extends BaseContainerComponentController implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    loading = false;

    @Input() apiTypes: string[] = [];
    @Input() selectedTabs?: string;
    @Input() headline: string = '';
    @Input() icon: string = '';
    @Input() monExport: string = '';

    // Properties matching original component
    public issues: any[] = [];
    public pages: any = [];
    public subheaderTranslation: string = '';
    public tabs: any[] = [];
    public promise: Promise<any> | null = null;
    public eventId = 0;

    constructor (
        private pageRepoService: PageRepoService,
        private translateService: TranslateService,
        private monNumberPipe: MonNumberPipe,
        private baseEntityService: BaseEntityService,
    ) {
        super({ table: true, pages: true });
    }

    ngOnInit (): void {
        this.eventId = this.monEventsService.addListener(
            ENTITY_CONSTANT.PAGE_UPDATED_EVENT as any,
            this.baseEntityService.updateEntryInArray(this, 'pages', 'id'),
        );
        this.initializeComponent();
    }

    ngOnDestroy (): void {
        if (this.eventId) {
            this.monEventsService.remove(this.eventId);
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initializeComponent (): void {
        this.issues = [];
        this.pages = [];
        this.search = '';
        this.activeTab = 'all';
        this.currentSort = { by: 'priority_score', direction: 'desc' };
        this.subheaderTranslation = '';

        // Set default values if not provided via inputs
        if (!this.icon) {
            this.icon = 'fas fa-window-maximize';
        }
        if (!this.headline) {
            this.headline = this.translateService.getString('HTML Pages');
        }

        this.setupTabs();
        this.getPage();
    }

    public override getPage (): void {
        this.loading = true;

        try {
            const params: any = {
                page: this.page,
                page_size: this.pageSize,
                search: this.search,
                type: this.activeTab === 'all' ? null : this.activeTab,
                'types[]': this.activeTab === 'all' ? this.apiTypes : null,
                sort_by: this.currentSort.by,
                sort_dir: this.currentSort.direction,
            };

            const updatedParams = this.updateParamsWithIssuesFilters(params);
            const finalParams = this.updateParamsWithFilters(updatedParams);

            this.promise = this.pageRepoService.getAll(finalParams).then((data) => {
                this.pages = data;
                this.getSubHeaderDescription();
            }).finally(() => {
                this.loading = false;
            });
        } catch (error) {
            this.loading = false;
        }
    }

    private getSubHeaderDescription (): void {
        const total = Number(this.pages.total) || 0;
        const formattedNumber = this.monNumberPipe.transform(total, '0,0');
        this.subheaderTranslation = this.translateService.getPlural(
            total,
            '1 content',
            '[[count]] content',
            { count: formattedNumber },
            '',
        );
    }

    public onFiltersChanges (changes: any): void {
        this.updateFilters(changes);
        if (changes.contains) {
            this.issues = changes.contains;
        } else {
            this.issues = [];
        }
        this.getPage();
    }

    private updateParamsWithIssuesFilters (params: any): any {
        for (let i = 0; i < this.issues.length; i++) {
            params[this.issues[i].id] = true;
        }
        return params;
    }

    private setupTabs (): void {
        const tabs: any[] = [{ name: 'All', faIcon: 'fas fa-copy', value: 'all' }];

        if (this.selectedTabs === 'documents') {
            const documentTabs = [
                {
                    name: this.translateService.getString('PDF Documents'),
                    value: 'pdf',
                },
                {
                    name: this.translateService.getString('Other Documents'),
                    value: 'document',
                },
            ];
            this.tabs = tabs.concat(documentTabs);
        } else {
            this.tabs = tabs;
        }
    }
}
